import { useEffect, useState } from 'react'
import {
  collectionGroup,
  doc,
  getDoc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore'
import type { OrgDoc, Role } from '@racedash/shared'
import { db } from '../lib/firebase'

export interface OrgSummary {
  id: string
  name: string
  kind: OrgDoc['kind']
  role: Role
  /** Needed to protect the primary owner from demotion or removal in the UI,
   *  mirroring the same guard in firestore.rules. */
  primaryOwnerUid: string
  isPrimaryOwner: boolean
}

/**
 * "Which organisations am I in?" — a collection-group query over members,
 * filtered on the denormalised `uid` field. Membership lives in Firestore
 * rather than in Auth custom claims deliberately: claims go stale until the ID
 * token refreshes, so removing someone would not take effect for up to an hour.
 */
export function useOrgs(uid: string | undefined) {
  const [orgs, setOrgs] = useState<OrgSummary[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!uid) {
      setOrgs(null)
      return
    }

    const membershipQuery = query(collectionGroup(db, 'members'), where('uid', '==', uid))

    return onSnapshot(
      membershipQuery,
      (snapshot) => {
        void (async () => {
          const summaries = await Promise.all(
            snapshot.docs.map(async (membership) => {
              // members/{uid} sits under orgs/{orgId}, so the grandparent is the org.
              const orgId = membership.ref.parent.parent?.id
              if (!orgId) return null

              const orgSnap = await getDoc(doc(db, 'orgs', orgId))
              const org = orgSnap.data() as OrgDoc | undefined
              if (!org) return null

              return {
                id: orgId,
                name: org.name,
                kind: org.kind,
                role: membership.get('role') as Role,
                primaryOwnerUid: org.primaryOwnerUid,
                isPrimaryOwner: org.primaryOwnerUid === uid,
              } satisfies OrgSummary
            }),
          )

          const resolved = summaries.filter((s): s is OrgSummary => s !== null)
          // Personal first, then alphabetical — the personal org is the one you
          // land in, so it should not move around as you join others.
          resolved.sort((a, b) => {
            if (a.kind !== b.kind) return a.kind === 'personal' ? -1 : 1
            return a.name.localeCompare(b.name)
          })
          setOrgs(resolved)
        })()
      },
      (err) => setError(err.message),
    )
  }, [uid])

  return { orgs, error }
}
